/**
 * Theme system core — Dmitry item 2 (settable/saveable themes). Provenance:
 * `agents/shared/decisions/frontend/2026-07-14-theme-system-decision.md`
 * (fleet-internal; not in this repo). The rules it set are stated below.
 *
 * "Choose the MODE, not the HUE" (В1=A): the user picks light / dark / system;
 * the ember accent + warm-neutral character are INVARIANT across every theme —
 * they live in the token blocks (packages/ui base.css), never here. So every
 * theme still reads as Agnivo by construction.
 *
 * base-3 now (System / Light / Dark). The signature themes (Тёплая-Sepia,
 * Приглушённая-Dim; В2=B) are a fast-follow that slot in as new `ThemePref`
 * values + `data-theme` token blocks with NO change to this resolver.
 *
 * Surface-agnostic: "system" resolution is delegated to a SystemSchemeDetector
 * the host supplies —
 *   mini-app                  → Telegram colorScheme (its own detector; inside a
 *                               Mini App the surrounding app's theme wins)
 *   web-ui / mobile / desktop → browserSystemSchemeDetector (prefers-color-scheme),
 *                               exported from app-core so the three browser-context
 *                               shells share one implementation.
 *
 * Supplying it is NOT optional for a shell that renders `routes`: SettingsScreen
 * calls useTheme(), which throws without a ThemeProvider above it.
 *
 * WAS: this said "(mini-app → Telegram colorScheme; web-ui → matchMedia)" in the
 * present tense while ONLY mini-app was wired — /settings was a crashed screen on
 * three shells for ~3 weeks. The identical sentence in index.ts was corrected in
 * the same MR that fixed the wiring, and this copy was missed (umnyi, reviewing
 * web!227). Noting that specifically, because THIS is the file someone reads to
 * learn the contract, and a corrected comment sitting next to an uncorrected copy
 * is how the wrong one wins on the next read.
 */
/** localStorage key for the persisted preference (client-only, per product scope). */
export const THEME_STORAGE_KEY = 'agnivo-theme';
const PREFS = ['system', 'light', 'dark', 'warm', 'dim'];
/** Read the persisted preference; defaults to 'system' (also on unknown/blocked storage). */
export function readThemePref() {
    try {
        const v = localStorage.getItem(THEME_STORAGE_KEY);
        return PREFS.includes(v ?? '') ? v : 'system';
    }
    catch {
        return 'system'; // storage blocked (private mode / SSR) → safe default
    }
}
/** Persist the preference. A blocked store is non-fatal (session-only apply still works). */
export function writeThemePref(pref) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, pref);
    }
    catch {
        /* storage blocked — the in-memory provider still applies for the session */
    }
}
/** Collapse (pref, host scheme) → the concrete theme the CSS keys on. */
export function resolveTheme(pref, systemScheme) {
    return pref === 'system' ? systemScheme : pref;
}
/** Set the `data-theme` attribute the CSS token blocks key on (light = the :root default). */
export function applyResolvedTheme(resolved) {
    document.documentElement.dataset.theme = resolved;
}
/**
 * Synchronous pre-paint application. Call from the host entry BEFORE React
 * renders so the first paint is already the correct theme — no light→dark flash.
 */
export function applyThemeAtBoot(detector) {
    applyResolvedTheme(resolveTheme(readThemePref(), detector.getScheme()));
}
//# sourceMappingURL=theme.js.map